/**
 * Custom hook for Organizations Management
 * Handles state and API calls for organization CRUD operations
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { organizationsService } from '@/services/organizations/organizationsService';
import type {
  Organization,
  OrganizationWithChildren,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
} from '@/types/organizations';
import { mergeDrivingLogOrgSettings } from '@/utils/organizationDrivingLogDefaults';
import { mergeReminderOrgSettings } from '@/utils/organizationReminderDefaults';
import { mergeOrganizationEmailSettings } from '@/utils/organizationEmailDefaults';
import { isOrgTopNode } from '@/utils/organizationFlags';
import type { EntityId } from '@/types/ids';
import { useTranslation } from '@/store/languageStore';
import { useAuthStore } from '@/store/authStore';
import toast from 'react-hot-toast';

const initialFormData: CreateOrganizationRequest = {
  name: '',
  description: '',
  parent_id: undefined,
  is_top_node: false,
  status: 'ACTIVE',
  auth_method: 'PASSWORD',
  allowed_domains: [],
  organization_identifier: '',
  portal_hostname: '',
  address: '',
  postNr: '',
  town: '',
  orgNr: '',
  contact: '',
  contacttel: '',
  email: '',
  carRental: false,
  is_booking_pool: false,
  costNo: '',
  max_recurring_bookings: undefined,
  booking_no_before: undefined,
  booking_no_after: undefined,
  booking_min_length_hours: undefined,
  booking_max_length_hours: undefined,
  booking_months_forward: undefined,
  cancellation_charge_hours: undefined,
  no_show_release_minutes: undefined,
  booking_rest_minutes: undefined,
  booking_min_lead_time_hours: undefined,
  booking_policy_html: undefined,
  booking_default_timeline_view: undefined,
  driving_log_settings: undefined,
  reminder_settings: undefined,
  support_forward_enabled: false,
  support_forward_email: '',
};

export const useOrganizationsData = () => {
  const { t } = useTranslation();
  const canEditPortal = useAuthStore((state) => {
    const flag = state.user?.is_global_admin;
    return flag === true || flag === 1 || flag === '1';
  });
  const getApiErrorMessage = (error: any): string =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    t('common.error');
  const toIdKey = (value: EntityId): string => String(value);
  const toPositiveInt = (value: EntityId, fieldName: string): number => {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      throw new Error(`Invalid ${fieldName}`);
    }
    return parsed;
  };
  
  // Main data state
  const [topNodes, setTopNodes] = useState<Organization[]>([]);
  const [totalTopNodes, setTotalTopNodes] = useState(0);
  const [totalTopNodePages, setTotalTopNodePages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [hierarchies, setHierarchies] = useState<Map<string, OrganizationWithChildren>>(new Map());
  const [directChildren, setDirectChildren] = useState<Map<string, Organization[]>>(new Map());
  const [loading, setLoading] = useState(true);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  // Move modal state
  const [moveTargetParentId, setMoveTargetParentId] = useState<EntityId | ''>('');
  const [moveExcludeIds, setMoveExcludeIds] = useState<EntityId[]>([]);

  // Parent selection state (for dropdowns)
  const [parentOptions, setParentOptions] = useState<Organization[]>([]);
  const [parentSearchTerm, setParentSearchTerm] = useState('');
  const [parentPage, setParentPage] = useState(1);
  const [parentTotalPages, setParentTotalPages] = useState(1);
  const [parentTotal, setParentTotal] = useState(0);
  const [loadingParents, setLoadingParents] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateOrganizationRequest>(initialFormData);

  const loadData = useCallback(async (page: number = currentPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const result = await organizationsService.getTopNodesWithDirectChildren(page, pageSize, search);
      setTopNodes(result.topNodes);
      setTotalTopNodes(result.total);
      setTotalTopNodePages(result.totalPages);
      const childrenMap = new Map<string, Organization[]>();
      Object.entries(result.directChildrenMap).forEach(([id, children]) => {
        childrenMap.set(String(id), children);
      });
      setDirectChildren(childrenMap);
      setHierarchies(new Map());
      if (search.trim()) {
        setExpandedNodes(new Set(result.topNodes.map((node) => String(node.id))));
      }
    } catch (error: any) {
      toast.error(t('organizations.loadDataError') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, t]);

  // Load data when page/search changes (server-side paginering/sökning)
  useEffect(() => {
    loadData(currentPage, searchTerm);
  }, [currentPage, searchTerm, loadData]);

  const loadParentOptions = async (page: number = 1, excludeId?: EntityId, excludeIds: EntityId[] = []) => {
    try {
      setLoadingParents(true);
      const result = await organizationsService.getAllOrganizationsPaginated(page, 50);
      const idsToExclude = excludeIds.length > 0 ? excludeIds : (excludeId ? [excludeId] : []);
      const filtered = idsToExclude.length > 0
        ? result.organizations.filter(o => !idsToExclude.includes(o.id))
        : result.organizations;
      setParentOptions(filtered);
      setParentTotalPages(result.totalPages);
      setParentTotal(result.total);
      setParentPage(page);
    } catch (error: any) {
      toast.error(t('organizations.loadDataError') + ': ' + error.message);
    } finally {
      setLoadingParents(false);
    }
  };

  const handleCreateOrganization = async () => {
    try {
      if (!formData.name) {
        toast.error(t('organizations.nameRequired'));
        return;
      }

      const parentId = formData.parent_id
        ? String(toPositiveInt(formData.parent_id, 'parent_id'))
        : undefined;
      const createPayload = {
        ...formData,
        parent_id: parentId,
        // Kundtoppnod under partner (t.ex. Dialect) kräver att kryssrutan följer med.
        // Utan förälder är organisationen alltid rot-toppnod.
        is_top_node: parentId ? Boolean(formData.is_top_node) : true,
      };
      if (!canEditPortal) {
        delete createPayload.portal_hostname;
      }
      await organizationsService.createOrganization(createPayload);

      toast.success(t('organizations.organizationCreated'));
      setShowCreateModal(false);
      setFormData(initialFormData);
      
      if (parentId) {
        setExpandedNodes(prev => new Set(prev).add(parentId));
      }
      
      setCurrentPage(1);
      await loadData(1, searchTerm);
    } catch (error: any) {
      toast.error(t('organizations.createError') + ': ' + error.message);
    }
  };

  const handleUpdateOrganization = async () => {
    if (!selectedOrganization) return;

    try {
      const willBeTopNode =
        formData.is_top_node !== undefined
          ? Boolean(formData.is_top_node)
          : isOrgTopNode(selectedOrganization);
      const updateData: UpdateOrganizationRequest = {
        name: formData.name || selectedOrganization.name,
        description: formData.description !== undefined ? formData.description : selectedOrganization.description,
        is_top_node: willBeTopNode,
        status: formData.status || selectedOrganization.status,
        address: formData.address !== undefined ? formData.address : selectedOrganization.address,
        postNr: formData.postNr !== undefined ? formData.postNr : selectedOrganization.postNr,
        town: formData.town !== undefined ? formData.town : selectedOrganization.town,
        orgNr: formData.orgNr !== undefined ? formData.orgNr : selectedOrganization.orgNr,
        contact: formData.contact !== undefined ? formData.contact : selectedOrganization.contact,
        contacttel: formData.contacttel !== undefined ? formData.contacttel : selectedOrganization.contacttel,
        email: formData.email !== undefined ? formData.email : selectedOrganization.email,
        carRental: formData.carRental !== undefined ? formData.carRental : selectedOrganization.carRental,
        is_booking_pool: formData.is_booking_pool !== undefined ? formData.is_booking_pool : Boolean(Number(selectedOrganization.is_booking_pool)),
        costNo: formData.costNo !== undefined ? formData.costNo : selectedOrganization.costNo,
        max_recurring_bookings: formData.max_recurring_bookings !== undefined ? formData.max_recurring_bookings : selectedOrganization.max_recurring_bookings,
        booking_no_before: formData.booking_no_before !== undefined ? formData.booking_no_before : selectedOrganization.booking_no_before,
        booking_no_after: formData.booking_no_after !== undefined ? formData.booking_no_after : selectedOrganization.booking_no_after,
        booking_min_length_hours: formData.booking_min_length_hours !== undefined ? formData.booking_min_length_hours : selectedOrganization.booking_min_length_hours,
        booking_max_length_hours: formData.booking_max_length_hours !== undefined ? formData.booking_max_length_hours : selectedOrganization.booking_max_length_hours,
        booking_months_forward: formData.booking_months_forward !== undefined ? formData.booking_months_forward : selectedOrganization.booking_months_forward,
        cancellation_charge_hours: formData.cancellation_charge_hours !== undefined ? formData.cancellation_charge_hours : selectedOrganization.cancellation_charge_hours,
        no_show_release_minutes:
          formData.no_show_release_minutes !== undefined
            ? formData.no_show_release_minutes
            : (selectedOrganization.no_show_release_minutes ?? 0),
        booking_rest_minutes:
          formData.booking_rest_minutes !== undefined
            ? formData.booking_rest_minutes
            : selectedOrganization.booking_rest_minutes,
        booking_min_lead_time_hours:
          formData.booking_min_lead_time_hours !== undefined
            ? formData.booking_min_lead_time_hours
            : selectedOrganization.booking_min_lead_time_hours,
        booking_default_timeline_view:
          formData.booking_default_timeline_view !== undefined
            ? formData.booking_default_timeline_view
            : selectedOrganization.booking_default_timeline_view,
        support_forward_enabled:
          formData.support_forward_enabled !== undefined
            ? Boolean(formData.support_forward_enabled)
            : Number(selectedOrganization.support_forward_enabled) === 1,
        support_forward_email:
          formData.support_forward_email !== undefined
            ? formData.support_forward_email
            : selectedOrganization.support_forward_email || '',
      };

      if (willBeTopNode) {
        const rawPolicy = formData.booking_policy_html ?? '';
        const textOnly = rawPolicy.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        updateData.booking_policy_html = textOnly === '' ? null : rawPolicy;
      }

      if (willBeTopNode) {
        updateData.auth_method = formData.auth_method || selectedOrganization.auth_method || 'PASSWORD';
        updateData.allowed_domains = formData.allowed_domains !== undefined ? formData.allowed_domains : selectedOrganization.allowed_domains || [];
        updateData.organization_identifier = formData.organization_identifier !== undefined ? formData.organization_identifier : selectedOrganization.organization_identifier || '';
        if (canEditPortal) {
          updateData.portal_hostname = formData.portal_hostname !== undefined ? formData.portal_hostname : selectedOrganization.portal_hostname || '';
        }
        updateData.driving_log_settings = mergeDrivingLogOrgSettings(
          formData.driving_log_settings ?? selectedOrganization.driving_log_settings ?? null
        );
        updateData.reminder_settings = mergeReminderOrgSettings(
          formData.reminder_settings ?? selectedOrganization.reminder_settings ?? null
        );
        updateData.email_settings = mergeOrganizationEmailSettings(
          formData.email_settings ?? selectedOrganization.email_settings ?? null
        );
      } else if (formData.driving_log_settings !== undefined) {
        updateData.driving_log_settings =
          formData.driving_log_settings === null
            ? null
            : mergeDrivingLogOrgSettings(formData.driving_log_settings);
      }

      const selectedOrganizationId = toPositiveInt(selectedOrganization.id, 'organization id');
      const updatedFromServer = await organizationsService.updateOrganization(selectedOrganizationId, updateData);
      toast.success(t('organizations.organizationUpdated'));
      setShowEditModal(false);
      setSelectedOrganization(null);

      const updatedOrg = {
        ...selectedOrganization,
        ...updateData,
        driving_log_settings:
          updatedFromServer?.driving_log_settings ?? updateData.driving_log_settings ?? selectedOrganization.driving_log_settings,
        reminder_settings:
          updatedFromServer?.reminder_settings ?? updateData.reminder_settings ?? selectedOrganization.reminder_settings,
        is_top_node: isOrgTopNode(updatedFromServer) || Boolean(updateData.is_top_node),
      };

      setTopNodes(prev =>
        prev.map(org =>
          org.id === selectedOrganization.id ? updatedOrg : org
        )
      );

      setDirectChildren(prev => {
        const newMap = new Map(prev);
        newMap.forEach((children, parentId) => {
          const updatedChildren = children.map(org =>
            org.id === selectedOrganization.id ? updatedOrg : org
          );
          newMap.set(parentId, updatedChildren);
        });
        return newMap;
      });

      setHierarchies(prev => {
        const updateOrgInTree = (node: OrganizationWithChildren): OrganizationWithChildren => {
          if (node.id === selectedOrganization.id) {
            return { ...node, ...updateData, is_top_node: updatedOrg.is_top_node };
          }
          if (node.children?.length) {
            return {
              ...node,
              children: node.children.map(updateOrgInTree),
            };
          }
          return node;
        };
        const newMap = new Map(prev);
        newMap.forEach((hierarchy, key) => {
          newMap.set(key, updateOrgInTree(hierarchy));
        });
        return newMap;
      });

      await loadData(currentPage, searchTerm);
    } catch (error: any) {
      toast.error(t('organizations.updateError') + ': ' + getApiErrorMessage(error));
    }
  };

  const handleDeleteOrganization = async (orgId: EntityId) => {
    try {
      const result = await organizationsService.deleteOrganization(toPositiveInt(orgId, 'organization id'));
      toast.success(
        result.deleteMode === 'hard'
          ? t('organizations.organizationDeletedHard')
          : t('organizations.organizationDeletedSoft')
      );
      
      // Rensa all cachad hierarki-data och ladda om från scratch
      setDirectChildren(new Map());
      setHierarchies(new Map());
      setExpandedNodes(new Set());
      await loadData(currentPage, searchTerm);
    } catch (error: any) {
      toast.error(`${t('organizations.deleteError')}: ${getApiErrorMessage(error)}`);
    }
  };

  const toggleExpand = useCallback(async (orgId: EntityId, _org?: OrganizationWithChildren) => {
    const orgKey = toIdKey(orgId);
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(orgKey)) {
      newExpanded.delete(orgKey);
      setExpandedNodes(newExpanded);
      return;
    }

    newExpanded.add(orgKey);
    setExpandedNodes(newExpanded);
    try {
      // Direkta barn är ocachade. Hierarki-endpointen cachas i 5 min och
      // skrev tidigare över listan så en nyss skapad undertoppnod försvann vid expand.
      const parsedOrgId = toPositiveInt(orgId, 'organization id');
      const children = await organizationsService.getDirectChildren(parsedOrgId);
      setDirectChildren(prev => new Map(prev).set(orgKey, children));
    } catch (error: any) {
      toast.error(t('organizations.loadChildrenError') + ': ' + error.message);
    }
  }, [expandedNodes, t]);

  const openEditModal = async (org: Organization) => {
    let source: Organization = org;
    try {
      source = await organizationsService.getOrganizationById(toPositiveInt(org.id, 'organization id'));
    } catch {
      // använd snapshot från trädet om API misslyckas
    }
    setSelectedOrganization(source);
    setFormData({
      name: source.name,
      description: source.description || '',
      is_top_node: isOrgTopNode(source),
      status: source.status,
      auth_method: source.auth_method || 'PASSWORD',
      allowed_domains: source.allowed_domains || [],
      organization_identifier: source.organization_identifier || '',
      portal_hostname: source.portal_hostname || '',
      address: source.address || '',
      postNr: source.postNr || '',
      town: source.town || '',
      orgNr: source.orgNr || '',
      contact: source.contact || '',
      contacttel: source.contacttel || '',
      email: source.email || '',
      carRental: source.carRental || false,
      is_booking_pool: Boolean(Number(source.is_booking_pool)),
      costNo: source.costNo || '',
      max_recurring_bookings: source.max_recurring_bookings ?? undefined,
      booking_no_before: source.booking_no_before ?? undefined,
      booking_no_after: source.booking_no_after ?? undefined,
      booking_min_length_hours: source.booking_min_length_hours ?? undefined,
      booking_max_length_hours: source.booking_max_length_hours ?? undefined,
      booking_months_forward: source.booking_months_forward ?? undefined,
      cancellation_charge_hours: source.cancellation_charge_hours ?? undefined,
      no_show_release_minutes: source.no_show_release_minutes ?? 0,
      booking_rest_minutes: source.booking_rest_minutes ?? undefined,
      booking_min_lead_time_hours: source.booking_min_lead_time_hours ?? undefined,
      booking_policy_html: source.booking_policy_html ?? '',
      booking_default_timeline_view: source.booking_default_timeline_view ?? false,
      driving_log_settings:
        source.driving_log_settings != null
          ? mergeDrivingLogOrgSettings(source.driving_log_settings)
          : isOrgTopNode(source)
            ? mergeDrivingLogOrgSettings(null)
            : undefined,
      reminder_settings: isOrgTopNode(source)
        ? mergeReminderOrgSettings(source.reminder_settings ?? null)
        : undefined,
      email_settings: isOrgTopNode(source)
        ? mergeOrganizationEmailSettings(source.email_settings ?? null)
        : undefined,
      support_forward_enabled: Number(source.support_forward_enabled) === 1,
      support_forward_email: source.support_forward_email || '',
    });
    await loadParentOptions(1, org.id);
    setShowEditModal(true);
  };

  const openCreateModal = async (parentId?: EntityId) => {
    setFormData({
      ...initialFormData,
      parent_id: parentId !== undefined ? String(toPositiveInt(parentId, 'parent_id')) : undefined,
      is_top_node: !parentId,
    });
    await loadParentOptions(1);
    setShowCreateModal(true);
  };

  const openMoveModal = async (org: Organization) => {
    setSelectedOrganization(org);
    setMoveTargetParentId('');
    
    const orgId = toPositiveInt(org.id, 'organization id');
    let allDescendants: EntityId[] = [org.id];
    try {
      const children = await organizationsService.getAllChildren(orgId);
      allDescendants = [org.id, ...children.map(c => c.id)];
    } catch {
      allDescendants = [org.id];
    }
    
    setMoveExcludeIds(allDescendants);
    await loadParentOptions(1, org.id, allDescendants);
    setShowMoveModal(true);
  };

  const handleMoveOrganization = async () => {
    if (!selectedOrganization || !moveTargetParentId) {
      toast.error(t('organizations.selectParent') || 'Välj en föräldraorganisation');
      return;
    }

    try {
      const wasTopNode = selectedOrganization.is_top_node;
      const selectedOrganizationId = toPositiveInt(selectedOrganization.id, 'organization id');
      const newParentId = toPositiveInt(moveTargetParentId, 'new_parent_id');
      
      await organizationsService.moveSubtree(selectedOrganizationId, {
        new_parent_id: String(newParentId),
      });
      
      toast.success(t('organizations.organizationMoved') || 'Organisation flyttad');
      setShowMoveModal(false);
      setSelectedOrganization(null);
      setMoveTargetParentId('');
      setMoveExcludeIds([]);
      setParentSearchTerm('');
      
      if (wasTopNode) {
        setTopNodes(prev => prev.filter(org => org.id !== selectedOrganization.id));
      }
      
      setCurrentPage(1);
      await loadData(1, searchTerm);
    } catch (error: any) {
      toast.error(t('organizations.moveError') || 'Fel vid flytt: ' + error.message);
    }
  };

  const handleSearch = useCallback((term: string) => {
    setSearchInput(term);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(term);
      setCurrentPage(1);
    }, 300);
  }, []);

  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  const goToPage = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const resetFormData = () => {
    setFormData(initialFormData);
  };

  return {
    // Data
    topNodes,
    filteredTopNodes: topNodes,
    totalTopNodes,
    totalTopNodePages,
    currentPage,
    pageSize,
    searchInput,
    searchTerm,
    handleSearch,
    expandedNodes,
    hierarchies,
    directChildren,
    loading,
    
    // Modal state
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    setShowEditModal,
    showMoveModal,
    setShowMoveModal,
    selectedOrganization,
    
    // Move modal
    moveTargetParentId,
    setMoveTargetParentId,
    moveExcludeIds,
    
    // Parent selection
    parentOptions,
    parentSearchTerm,
    setParentSearchTerm,
    parentPage,
    parentTotalPages,
    parentTotal,
    loadingParents,
    loadParentOptions,
    
    // Form
    formData,
    setFormData,
    resetFormData,
    
    // Actions
    loadData,
    handleCreateOrganization,
    handleUpdateOrganization,
    handleDeleteOrganization,
    handleMoveOrganization,
    toggleExpand,
    openEditModal,
    openCreateModal,
    openMoveModal,
    goToPage,
  };
};
